import { ArrowLeft } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { PostgridBreadcrumbs } from '@/components/postgrid/common/PostgridBreadcrumbs';
import { PostgridSubNavigation } from '@/components/postgrid/common/PostgridSubNavigation';
import {
  ContactWizard,
  type ContactWizardResult,
} from '@/components/postgrid/contact-wizard';
import { Button } from '@/components/ui/button';
import { usePostgridContactsCreateMutation } from '@/services/api/postgridService';

/**
 * CreateContactPage
 *
 * Global page that allows users to create a new PostGrid contact. Mirrors the
 * behaviour of the ChequeWizardPage within MatterMailTab but for contacts.
 */
export const CreateContactPage: React.FC = () => {
  const navigate = useNavigate();

  // RTK Query mutation for creating contacts
  const [createContact, { isLoading }] = usePostgridContactsCreateMutation();

  const handleCreate = async (result: ContactWizardResult) => {
    try {
      await createContact({
        createPostgridContact: result.payload,
        skipVerification: result.skipVerification,
      }).unwrap();
      toast.success('Contact created successfully');
      // Redirect back to contacts list so the user can see the new contact
      navigate('/postgrid/contacts');
    } catch (err) {
      /* eslint-disable no-console */
      console.error(err);
      toast.error('Failed to create contact');
    }
  };

  const handleCancel = () => navigate(-1);

  return (
    <div className="bg-zinc-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200">
        <div className="px-6 py-4">
          <div className="space-y-3">
            <PostgridBreadcrumbs />
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={handleCancel} className="gap-2">
                <ArrowLeft className="h-4 w-4" /> Back to Contacts
              </Button>
            </div>
          </div>
        </div>
        <PostgridSubNavigation className="px-6" />
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="bg-white rounded-lg border border-zinc-200 p-6">
          <ContactWizard
            onSuccess={handleCreate}
            onCancel={handleCancel}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateContactPage;
